import { formatDateTime } from '@yes.technology/react-toolkit'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import { useSiteState } from 'siteState/shared'
import useYesObject from 'yesObject/shared/hooks/useYesObject'
import { AppliedFilterFieldValuesProps } from '../../AppliedFilters/AppliedFilters'
import { RefreshDataOptionsProps } from '../Cockpit.types'
import CardChart, { CardChartProps } from './CardChart'
import { DataChart } from './ChartWrapper'

const DEFAULT_REFRESH_INTERVAL = 30000

export interface PollingCardChartProps
  extends Pick<CardChartProps, 'title' | 'chartModel'> {
  refreshDataOptions: RefreshDataOptionsProps
  measurementUnit?: string
}

type ResultObject = {
  dataset: DataChart
  complement?: string
  date_modification_row: string
}

const PollingCardChart = ({
  refreshDataOptions,
  chartModel,
  title,
  measurementUnit
}: PollingCardChartProps) => {
  const { yesObject: data, isFetched } = useYesObject({
    objectclassUUID: refreshDataOptions?.uuid_objectclass,
    objectUUID: refreshDataOptions?.uuid_element,
    projectFields: {
      complement: 1,
      dataset: 1,
      date_modification_row: 1
    },
    enabled:
      !!refreshDataOptions?.uuid_element &&
      !!refreshDataOptions?.uuid_objectclass,
    refetchInterval:
      refreshDataOptions?.refresh_interval ?? DEFAULT_REFRESH_INTERVAL
  })

  const { t } = useTranslation()

  const intlLocale = t('intl-locale') as AllowedLocales

  const [_, setCockpitAppliedFilters] = useSiteState<
    AppliedFilterFieldValuesProps[]
  >('cockpit-applied-filters')

  const formattedData = useMemo(() => {
    const resultObject: ResultObject = {
      dataset: [],
      complement: '',
      date_modification_row: ''
    }

    if (!isFetched || !data) {
      return null
    }

    try {
      if (data?.dataset) {
        resultObject.dataset =
          typeof data.dataset === 'string' ? JSON.parse(data.dataset) : {}

        if (
          typeof resultObject.date_modification_row &&
          typeof resultObject.date_modification_row === 'string'
        ) {
          resultObject.date_modification_row = formatDateTime(
            new Date(data.date_modification_row),
            {
              locale: intlLocale,
              format: 'date-time',
              timezone: window.waTimezone
            }
          ).replace('|', '-')
        }

        resultObject.complement = data.complement ?? ''

        return resultObject
      }

      return null
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return null
    }
  }, [data, intlLocale, isFetched])

  useEffect(() => {
    if (formattedData) {
      setCockpitAppliedFilters([
        {
          label: t('cockpit.weight_scale.model'),
          operator: '=',
          value: formattedData?.complement ?? ''
        },
        {
          label: t('cockpit.weight_scale.weighing-reference-period'),
          operator: '=',
          value: formattedData?.date_modification_row
        }
      ])
    }
  }, [formattedData, setCockpitAppliedFilters, t])

  if (!formattedData?.dataset?.length) {
    return null
  }

  return (
    <CardChart
      chartModel={chartModel}
      title={title}
      data={formattedData.dataset}
      measurementUnit={measurementUnit}
    />
  )
}

export default PollingCardChart
